import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { adjustedIndex, supportedDenominations } from 'config/denomination';
import { forceReloginGames } from 'config/games';
import { getDenomination, saveDenomination } from "localapi";
import { loginUser, loginUserAuto, loginUserDemo } from '../auth';

const bonusGameIds = {
    '158': 401,
    '726': 402,
    '577': 403,
    '711': 404,
};

export const updateBalance = createAsyncThunk(
    'user/updateBalance',
    async (_, { extra, rejectWithValue }) => {
        const { socket } = extra;
        try {
            const balanceRes = await socket.getBalance();
            const cashbackRes = await socket.getCashbackBonus();
            return { ...balanceRes, cashbackBonus: cashbackRes.cashbackBonus };
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const syncBalance = createAsyncThunk(
    'user/syncBalance',
    async (_, { extra, rejectWithValue }) => {
        const { socket } = extra;
        try {
            const balanceRes = await socket.syncBalance();
            const cashbackRes = await socket.getCashbackBonus();
            return { ...balanceRes, cashbackBonus: cashbackRes.cashbackBonus };
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const takeCashbackBonus = createAsyncThunk(
    'user/takeCashbackBonus',
    async (_, { extra, dispatch, rejectWithValue }) => {
        const { socket } = extra;
        try {
            await socket.takeCashbackBonus();
            dispatch(syncBalance());
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const updateBalanceAndNotify = createAsyncThunk(
    'user/updateBalanceAndNotify',
    async ({ balance, value }, { extra, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {
            const { user } = getState();
            const oldCredits = Math.floor((balance - value) / user.denomination);
            const res = await socket.getBalance();
            const newCredits = Math.floor(res.balance / res.denomination);
            socket.socket.onChangeBalance({
                value: newCredits - oldCredits
            });
            return res;
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const restoreDenomination = createAsyncThunk(
    'user/restoreDenomination',
    async (_, { extra, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {
            const { auth, user } = getState();
            if (user.playingGameId !== null) {
                return user.denomination;
            }
            const restoredIndex = await getDenomination(auth.uid);
            let nextIndex = restoredIndex;
            if (nextIndex > 0) {
                nextIndex = adjustedIndex(restoredIndex, user.denominationMin, user.denominationMax);
                if (restoredIndex !== nextIndex) {
                    await saveDenomination(auth.uid, nextIndex);
                }
                await socket.setDenomination(nextIndex);
            }
            return supportedDenominations[nextIndex];
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const changeDenomination = createAsyncThunk(
    'user/changeDenomination',
    async ({ nextIndex }, { extra, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {
            const { auth } = getState();
            await socket.setDenomination(nextIndex);
            await saveDenomination(auth.uid, nextIndex);
            return supportedDenominations[nextIndex];
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const updateCurrency = createAsyncThunk(
    'user/updateCurrency',
    async (_, { extra, rejectWithValue }) => {
        const { socket } = extra;
        try {
            return await socket.getCurrencyCode();
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const launchGame = createAsyncThunk(
    'user/launchGame',
    async (params, { extra, getState, rejectWithValue }) => {
        const { socket } = extra;
        let { gameId } = params;
        try {
            socket.inGameMode = true;
            socket.saveHandlers();
            const bonusGameId = bonusGameIds[gameId]
            if (bonusGameId !== undefined) {
                const oldOnBonusReceivedHandler = socket.onBonusReceived;
                const bonusInfo = await socket.getBonusInfo();
                socket.onBonusReceived = oldOnBonusReceivedHandler;
                if (bonusInfo?.gamePermit === bonusGameId && bonusInfo.freespinsLeft > 0) {
                    gameId = bonusGameId;
                }
            }
            const { games } = getState();
            const gameProps = games.gameEntities[gameId];
            if (gameProps?.vendor === "pragmatic" && gameId < 4000) {
                socket.close();
            }
            if (forceReloginGames.includes(gameId)) {
                socket.close();
            }
            return { ...params, gameId };
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const closeGame = createAsyncThunk(
    'user/closeGame',
    async (_, { extra, dispatch, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {
            socket.inGameMode = false;
            socket.restoreHandlers();
            if (socket.isConnected) {
                const { auth, user } = getState();

                await socket.exitGame();
                await socket.getBonusInfo();

                const restoredIndex = await getDenomination(auth.uid);
                const nextIndex = adjustedIndex(restoredIndex, user.denominationMin, user.denominationMax);
                dispatch(changeDenomination({ nextIndex })).then(() => {
                    dispatch(syncBalance());
                });
            }
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const init = createAsyncThunk(
    'user/init',
    async (_, { extra, dispatch, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {
            socket.inGameMode = false;
            const { auth, user } = getState();

            await socket.getBonusInfo();

            const restoredIndex = await getDenomination(auth.uid);
            const nextIndex = adjustedIndex(restoredIndex, user.denominationMin, user.denominationMax);
            dispatch(changeDenomination({ nextIndex })).then(() => {
                dispatch(syncBalance());
            });
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const getBonusInfo = createAsyncThunk(
    'user/getBonusInfo',
    async (_, { extra, rejectWithValue }) => {
        const { socket } = extra;
        try {
            await socket.getBonusInfo();
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const userSlice = createSlice({
    name: 'user',
    initialState: {
        balanceReady: false,
        balanceLocked: false,
        balance: 0,
        denomination: 0,
        denominationMin: 0,
        denominationMax: 0,
        denominationChangeAllowed: false,
        showDenomination: false,
        depositBonus: 0,
        cashbackBonus: 0,
        depositUrl: "",
        maxBetPerLine: 0,
        creditsLimit: 0,
        currencyCode: 0,
        currencyName: "",
        switching: 0,
        stretching: 0,
        playingGameId: null,
        playingGameSymbol: null,
        playMode: 'real',
        playingTournamentId: null,
        refererHallId: null,
    },
    reducers: {
        resetBalance(state) {
            if (state.balanceLocked) {
                return;
            }
            state.balance = 0;
            state.depositBonus = 0;
            state.cashbackBonus = 0;
        },
        setDenominationLimits(state, action) {
            const { denominationMin, denominationMax } = action.payload;
            if (denominationMin) {
                state.denominationMin = denominationMin;
            }
            if (denominationMax) {
                state.denominationMax = denominationMax;
            }
        },
        setRefererHall(state, action) {
            const { hallId } = action.payload;
            state.refererHallId = hallId;
        },
        lockBalance(state, action) {
            const lock = action.payload;
            state.balanceLocked = lock;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(updateCurrency.fulfilled, (state, { payload }) => {
            state.currencyCode = payload.currencyCode;
            state.currencyName = payload.currencyName;
            state.showDenomination = payload.showDenomination;
        });
        builder.addCase(launchGame.fulfilled, (state, { payload }) => {
            const { gameId, requestDemo, tournamentId, symbol } = payload;
            state.playingGameId = gameId;
            state.playingGameSymbol = symbol;
            state.playMode = requestDemo ? 'demo' : 'real';
            state.playingTournamentId = tournamentId;
        });
        builder.addCase(closeGame.pending, (state) => {
            state.playingGameId = null;
            state.playingGameSymbol = null;
            state.playMode = 'real';
        });
        builder.addMatcher(isAnyOf(updateBalance.fulfilled, syncBalance.fulfilled), (state, { payload }) => {
            state.balanceReady = true;
            if (state.balanceLocked) {
                return;
            }
            state.balance = payload.balance;
            state.denomination = payload.denomination;
            state.depositBonus = payload.depositBonus;
            state.cashbackBonus = payload.cashbackBonus;
        });
        builder.addMatcher(isAnyOf(updateBalance.rejected, syncBalance.rejected), (state, { payload }) => {
            console.warn('updateBalance rejected:', payload);
            state.balanceReady = true;
            if (state.balanceLocked) {
                return;
            }
            state.balance = 0;
            state.depositBonus = 0;
            state.cashbackBonus = 0;
        });
        builder.addMatcher(isAnyOf(changeDenomination.fulfilled, restoreDenomination.fulfilled), (state, { payload }) => {
            state.denomination = payload;
        });
        builder.addMatcher(isAnyOf(loginUser.fulfilled, loginUserAuto.fulfilled, loginUserDemo.fulfilled), (state, { payload }) => {
            const { loginData } = payload;
            state.denominationChangeAllowed = loginData.denominationEnable;
            state.denominationMin = loginData.denominationmin;
            state.denominationMax = loginData.denominationmax;
            state.maxBetPerLine = loginData.maxBetPerLine;
            state.creditsLimit = loginData.creditsLimit;
            state.switching = loginData.switching;
            state.stretching = loginData.stretching;
        });
    },
});

export const userSelector = (state) => state.user;
export const { lockBalance, resetBalance, setDenominationLimits, setRefererHall } = userSlice.actions;
export default userSlice.reducer;